import Head from "next/head";
import Link from "next/link";
import { Box, Stack } from "@mui/material";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import { createJsxLink } from "src/utils/create-jsx-link";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    group: "Games/Months Included",
    question: "Which games are included with my Ballpark Pass?",
    answer: (
      <Box pt={0.5} pl={1}>
        Passes include a Standing Room Only ticket to all eligible
        regular-season home games. Opening Day (April 3) is excluded.
      </Box>
    ),
  },
  {
    group: "Games/Months Included",
    question:
      "How do I opt out of next month if I don't want to continue my Ballpark Pass (monthly pass only)?",
    answer: (
      <Box pt={0.5} pl={1}>
        Log into your Ballpark Pass account, go to{" "}
        <Link
          target="_blank"
          href="https://pass.attendevents.com/coloradorockies_pass/manage_subscription"
        >
          Manage Subscription
        </Link>{" "}
        and opt out by the 25th of the prior month.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "Am I guaranteed a ticket to every game?",
    answer: (
      <Box pt={0.5} pl={1}>
        Yes, your Ballpark Pass provides a Standing Room Only ticket to every
        eligible game.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "Where can I go with a Standing Room Ticket?",
    answer: (
      <Box pt={0.5} pl={1}>
        Access includes the Rooftop and, for some games, open seating in
        Sections U310-U314.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "Is there any loaded concession value on a Ballpark Pass ticket?",
    answer: (
      <Box pt={0.5} pl={1}>
        No, tickets do NOT include concession value.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question:
      "How do I claim my tickets? When does the check-in open before each game?",
    answer: (
      <Box pt={0.5} pl={1}>
        You must opt in for each game. A text is sent at 9:00 AM on the first
        day of a homestand, allowing you to check into all available games. You
        will need to log in to your Ballpark Pass account to check in for each
        game. If you opt in, you&apos;ll receive a text with your ticket link
        three hours before first pitch.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "Can I purchase multiple Ballpark Passes?",
    answer: (
      <Box pt={0.5} pl={1}>
        Yes, up to four passes per account. You can also buy up to four guest
        passes per game during the opt-in window (if available). Violators will
        be removed from the program.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "Can I bring a guest with my Ballpark Pass?",
    answer: (
      <Box pt={0.5} pl={1}>
        Yes, you can purchase up to four guest passes per game based on
        availability (starting at $10 per pass). Guest passes cannot be
        forwarded, and all guests must enter together.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "Can I upgrade my seat?",
    answer: (
      <Box pt={0.5} pl={1}>
        Seat upgrades may be available when you opt in for a homestand. If you
        upgrade, the change applies to all pass seats in your account.
      </Box>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "What if I lose the text message with my pass?",
    answer: (
      <>
        Login to your{" "}
        <Link href="https://bp.mlb.com/Wallet" target="_blank">
          Ballpark app
        </Link>{" "}
        to access your tickets.
      </>
    ),
  },
  {
    group: "Other",
    question: "What happens if I don't check-in for a game?",
    answer: (
      <Box pt={0.5} pl={1}>
        There are no penalties, but you will not receive a ticket unless you opt
        in. If you miss the check-in window, you may activate your pass up to
        one hour after first pitch, subject to availability. Guest passes and
        upgrades will not be available after the opt-in window closes.
      </Box>
    ),
  },
  {
    group: "Other",
    question: "How do I access my tickets for entry to Coors Field?",
    answer: (
      <Box pt={0.5} pl={1}>
        To access your ticket/barcodes:
        <Box my={1} />
        Log in to the{" "}
        <Link href="https://bp.mlb.com/Wallet" target="_blank">
          MLB Ballpark app
        </Link>{" "}
        <Box my={1} />
        Select the Tickets tab
        <Box my={1} />
        Choose the game
        <Box my={1} />
        Tap each ticket to view the individual barcode (3 hours prior to game
        time) .
      </Box>
    ),
  },
  {
    group: "Other",
    question: "Can I sell, transfer, or get a refund for my Ballpark Pass?",
    answer: (
      <Box pt={0.5} pl={1}>
        No, Ballpark Passes are non-transferable, non-refundable, and cannot be
        resold. Violators will be removed from the program.
      </Box>
    ),
  },
  {
    group: "Other",
    question: "Am I eligible for promotional giveaways?",
    answer: (
      <Box pt={0.5} pl={1}>
        Yes, Ballpark Pass holders can receive gate giveaways (while supplies
        last). Special Ticket Package items require an additional ticket
        purchase.
      </Box>
    ),
  },
  {
    group: "Other",
    question: "What other benefits are included?",
    answer: (
      <Box pt={0.5} pl={1}>
        The Ballpark Pass does not provide membership benefits.
      </Box>
    ),
  },
  {
    group: "Other",
    question:
      "Does the Ballpark Pass include postseason games or other events?",
    answer: (
      <Box pt={0.5} pl={1}>
        No, the pass is valid for regular-season home games only.
      </Box>
    ),
  },
  {
    group: "Other",
    question: "Can I purchase the pass if I don't have a smartphone?",
    answer: (
      <Box pt={0.5} pl={1}>
        No, the Ballpark Pass is mobile-only. No printed tickets will be issued.
      </Box>
    ),
  },
];

// monthly pass
export const TERMS_AND_CONDITION = () => {
  return (
    <>
      I understand that I am agreeing to a monthly subscription through the rest
      of the season. It will renew at $39.99** per pass each month through
      September 2026 (**June Ballpark Pass charge will be $49.99) unless I
      choose to cancel my subscription during the given opt-out period. The
      Colorado Rockies Ballpark Pass (“Ballpark Pass”) provides access to most
      Colorado Rockies (“Club”) regular season home games during the 2026
      season. All Ballpark Pass tickets are delivered via the MLB Ballpark app
      on your mobile device. Ballpark Passes may only be scanned by the holder
      of the associated MLB Ballpark app account, and cannot be forwarded,
      shared, exchanged or resold. Each Ballpark Pass ticket will be visible in
      the MLB Ballpark app and available to scan beginning approximately two (2)
      hours prior to the first pitch of the respective game. Screenshots will
      not be accepted as a form of entry into Coors Field. Limit of four (4)
      Ballpark Passes may be purchased by any person, household or entity.
      <br />
      <br />
      Ballpark Pass will be automatically charged or renewed each month during
      the 2026 season unless otherwise cancelled as set forth below. By
      purchasing the Ballpark Pass, you authorize the Club to automatically
      charge the Ballpark Pass yearly or monthly fee to the credit or debit card
      specified online in your account, or any subsequent credit or debit
      card(s) you provide to the Club (your “Card”) on a month-by-month basis
      during the 2026 regular season (each charge, a “Charge”). Each Charge of
      your Card of the yearly or monthly fee will occur prior to the month in
      which the Ballpark Pass is eligible. If the Card you provide to the Club
      expires or is not valid, then you must immediately provide the Club with a
      substitute card account or the Club may cancel your Ballpark Pass. On the
      15th day of any calendar month, Monthly Ballpark Pass customers will
      receive a text message notifying you that the opt-out window for managing
      your monthly Ballpark Pass subscription will open. Go to the Manage
      Subscription tab when logged into your account and select the Cancel.
      <br />
      <br />
      The terms and conditions of your Ballpark Pass and policies applicable to
      use are subject to the Colorado Rockies Regular Season Ticket Terms &
      Conditions as well as policies applicable to use including the MLB Terms
      of Use and Privacy Policy. Please visit the Ballpark Pass FAQs for
      additional information. Ticket brokers are not eligible for this offer.
      Any attempt to resell the Ballpark Pass voids the Ballpark Pass. All sales
      are final. There are no refunds or exchanges. Ballpark Passes are limited
      and available while supplies last.
    </>
  );
};

// season pass
export const TERMS_AND_CONDITION_OPT_OUT_RESTRICTED = () => {
  return (
    <>
      I understand that I am agreeing to a monthly subscription through the rest
      of the season with no opt out.{" "}
      <b>
        It is a total of $199 broken out into 6 installments of $33.16 billed
        monthly through August 2025
      </b>
      . The Colorado Rockies Ballpark Pass (“Ballpark Pass”) provides access to
      most Colorado Rockies (“Club”) regular season home games during the 2025
      season. Please note excluded games are 4/4 and 5/23-25 and that the
      Ballpark Pass provides for a Standing Room Only ticket. All Ballpark Pass
      tickets are delivered via the MLB Ballpark app on your mobile device.
      Ballpark Passes may only be scanned by the holder of the associated MLB
      Ballpark app account, and cannot be forwarded, shared, exchanged or
      resold. Each Ballpark Pass ticket barcode will be visible in the MLB
      Ballpark app and available to scan beginning approximately two (2) hours
      prior to the first pitch of the respective game. Screenshots will not be
      accepted as a form of entry into Coors Field. Limit of four (4) Ballpark
      Passes may be purchased by any person, household or entity.
      <br />
      <br />
      Ballpark Pass will be automatically charged or renewed each month during
      the 2025 season unless otherwise cancelled as set forth below. By
      purchasing the Ballpark Pass, you authorize the Club to automatically
      charge the Ballpark Pass yearly or monthly fee to the credit or debit card
      specified online in your account, or any subsequent credit or debit
      card(s) you provide to the Club (your “Card”) on a month-by-month basis
      during the 2025 regular season (each charge, a “Charge”). Each Charge of
      your Card of the yearly or monthly fee will occur prior to the month in
      which the Ballpark Pass is eligible. If the Card you provide to the Club
      expires or is not valid, then you must immediately provide the Club with a
      substitute card account or the Club may cancel your Ballpark Pass. On the
      15th day of any calendar month, Monthly Ballpark Pass customers will
      receive a text message notifying you that the opt-out window for managing
      your monthly Ballpark Pass subscription will open. Go to the Manage
      Subscription tab when logged into your account and select the Cancel.
      <br />
      <br />
      The terms and conditions of your Ballpark Pass and policies applicable to
      use are subject to the Colorado Rockies Regular Season Ticket Terms &
      Conditions as well as policies applicable to use. Please visit the
      Ballpark Pass FAQs for additional information. Ticket brokers are not
      eligible for this offer. Any attempt to resell the Ballpark Pass voids the
      Ballpark Pass. All sales are final.{" "}
      <b>There are no refunds or exchanges</b>. Ballpark Passes are limited and
      available while supplies last.
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/rockies/tickets/ticket-back-disclaimer"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        Colorado Rockies Terms and Conditions
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1800332046857750');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1800332046857750&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const REGISTRATION_TERMS_AND_CONDITIONS = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from Official Colorado Rockies Website | MLB.com. You may unsubscribe
    at any time. By checking this box you agree to receive additional commercial
    emails from Official Colorado Rockies Website | MLB.com and The Official
    Site of Major League Baseball. The terms and conditions of your ticket and
    policies applicable to its use are updated to address circumstances related
    to COVID-19 and health and safety. Further, due to the COVID-19 pandemic,
    scheduling may be impacted; games may be canceled, rescheduled, or postponed
    or games may be held with limited capacity. Upon completing your purchase by
    selecting “Purchase Tickets” you acknowledge and agree that your credit card
    will be charged. By clicking “Purchase Tickets”, you agree to the{" "}
    {createJsxLink(
      "MLB Privacy Policy",
      "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
      {
        target: "_blank",
      }
    )}
    ,{" "}
    {createJsxLink("Terms of Use", "https://www.mlb.com/terms-of-use", {
      target: "_blank",
    })}{" "}
    and to receive your ticket(s) via an SMS text message to the mobile #
    associated with your Rockies account. Message and data rates may apply.
    Tickets will be made available in the MLB Ballpark mobile application only.
    MLB.com and supported mobile device required.
  </>
);

export const TERMS_AND_CONDITION_ADD_ON = () => {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from rockies.com. You may unsubscribe at any time. By
      checking this box you agree to receive additional commercial emails from
      rockies.com and MLB.com.
      <br />
      <br />
      The terms and conditions of your ticket and policies applicable to its use
      are updated to address circumstances related to COVID-19 and health and
      safety. By clicking the Purchase Tickets button, you agree to accept all{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/rockies/tickets/ticket-back-disclaimer"
        }
        target={"_blank"}
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      <br />
      <br />
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target={"_blank"}
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
        }
        target={"_blank"}
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Rockies account. Message and data rates may apply
      Tickets will be made available in the MLB Ballpark mobile application
      only. MLB.com and supported mobile device required.
    </>
  );
};
